import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import MultiMediaUpload from '../components/MultiMediaUpload';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, AlertCircle, FolderPlus, Video, Image as ImageIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  getStoreByOwner,
  getProductsByStore,
  createProduct,
  updateProduct,
  deleteProduct,
  canAddProduct,
  getStoreCategories,
  createStoreCategory,
  deleteStoreCategory
} from '../lib/firebaseService';

export default function Products() {
  const { firebaseUser } = useAuth();
  const [products, setProducts] = useState([]);
  const [store, setStore] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [limitInfo, setLimitInfo] = useState({ canAdd: true, currentCount: 0, maxProducts: 10 });
  const [newCategoryName, setNewCategoryName] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    media: [],
    stock: '',
    category_id: ''
  });

  useEffect(() => {
    if (firebaseUser) {
      loadData();
    }
  }, [firebaseUser]);

  const loadData = async () => {
    try {
      setPageLoading(true);
      
      const userStore = await getStoreByOwner(firebaseUser.uid);
      setStore(userStore);
      
      if (userStore) {
        const [productsList, limit, categoriesList] = await Promise.all([
          getProductsByStore(userStore.id),
          canAddProduct(firebaseUser.uid, userStore.id),
          getStoreCategories(userStore.id)
        ]);
        
        setProducts(productsList);
        setLimitInfo(limit);
        setCategories(categoriesList);
      }
    } catch (error) {
      console.error('Məlumatlar yüklənə bilmədi:', error);
      toast.error('Məlumatlar yüklənə bilmədi');
    } finally {
      setPageLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!store) {
      toast.error('Əvvəlcə mağaza yaradın');
      return;
    }

    if (!editingProduct && !limitInfo.canAdd) {
      toast.error(`Məhsul limitinə çatdınız (${limitInfo.maxProducts}). Admin ilə əlaqə saxlayın.`);
      return;
    }

    setLoading(true);
    try {
      // Get main image from media array
      const mainImage = formData.media.length > 0 
        ? (formData.media[0].url || formData.media[0]) 
        : '';

      const data = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        image_url: mainImage,
        media: formData.media,
        stock: parseInt(formData.stock) || 0,
        category_id: formData.category_id || null,
        store_id: store.id
      };

      if (editingProduct) {
        await updateProduct(editingProduct.id, data);
        toast.success('Məhsul yeniləndi');
      } else {
        await createProduct(data);
        toast.success('Məhsul əlavə edildi');
      }

      setDialogOpen(false);
      setFormData({ name: '', description: '', price: '', media: [], stock: '', category_id: '' });
      setEditingProduct(null);
      loadData();
    } catch (error) {
      console.error('Əməliyyat uğursuz:', error);
      toast.error(error.message || 'Əməliyyat uğursuz oldu');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    // Convert old single image to media array if needed
    let mediaArray = product.media || [];
    if (mediaArray.length === 0 && product.image_url) {
      mediaArray = [{ url: product.image_url, type: 'image' }];
    }
    
    setFormData({
      name: product.name,
      description: product.description || '',
      price: product.price?.toString() || '',
      media: mediaArray,
      stock: product.stock?.toString() || '0',
      category_id: product.category_id || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Məhsulu silmək istədiyinizə əminsiniz?')) return;
    
    try {
      await deleteProduct(productId);
      toast.success('Məhsul silindi');
      loadData();
    } catch (error) {
      toast.error('Əməliyyat uğursuz oldu');
    }
  };

  const openNewDialog = () => {
    if (!limitInfo.canAdd) {
      toast.error(`Məhsul limitinə çatdınız (${limitInfo.maxProducts}). Admin ilə əlaqə saxlayın.`);
      return;
    }
    setEditingProduct(null);
    setFormData({ name: '', description: '', price: '', media: [], stock: '', category_id: '' });
    setDialogOpen(true);
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    
    try {
      await createStoreCategory({
        store_id: store.id,
        name: newCategoryName.trim(),
        order: categories.length
      });
      toast.success('Kateqoriya əlavə edildi');
      setNewCategoryName('');
      setCategoryDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Kateqoriya əlavə edilə bilmədi');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Bu kateqoriyanı silmək istədiyinizə əminsiniz?')) return;
    
    try {
      await deleteStoreCategory(categoryId);
      toast.success('Kateqoriya silindi');
      loadData();
    } catch (error) {
      toast.error('Kateqoriya silinə bilmədi');
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || '';
  };

  const getMediaCount = (product) => {
    const media = product.media || [];
    const images = media.filter(m => m.type === 'image' || (!m.type && !m.url?.includes('.mp4'))).length;
    const videos = media.filter(m => m.type === 'video' || m.url?.includes('.mp4')).length;
    
    // Count old single image
    if (media.length === 0 && product.image_url) {
      return { images: 1, videos: 0 };
    }
    
    return { images, videos };
  };

  if (pageLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Yüklənir...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!store) {
    return (
      <DashboardLayout>
        <div className="space-y-6" data-testid="products-page">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-800 mb-1">Mağaza yaradılmayıb</h3>
                <p className="text-sm text-yellow-700 mb-3">
                  Məhsul əlavə etmək üçün əvvəlcə mağazanızı yaratmalısınız.
                </p>
                <a
                  href="/dashboard/store"
                  className="inline-flex items-center justify-center h-10 px-6 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700 transition-all"
                >
                  Mağaza yarat
                </a>
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="products-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold">Məhsullar</h1>
            <p className="text-muted-foreground mt-1">
              Mağazanızın məhsullarını idarə edin ({limitInfo.currentCount}/{limitInfo.maxProducts})
            </p>
          </div>
          <div className="flex gap-2">
            <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <FolderPlus className="w-4 h-4 mr-2" />
                  Kateqoriya
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Kateqoriyalar</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Kateqoriya adı"
                    />
                    <Button onClick={handleAddCategory}>Əlavə et</Button>
                  </div>
                  {categories.length > 0 ? (
                    <div className="space-y-2">
                      {categories.map((cat) => (
                        <div key={cat.id} className="flex items-center justify-between p-2 bg-muted rounded">
                          <span>{cat.name}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => handleDeleteCategory(cat.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Heç bir kateqoriya yoxdur
                    </p>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openNewDialog} disabled={!limitInfo.canAdd}>
                  <Plus className="w-4 h-4 mr-2" />
                  Məhsul əlavə et
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingProduct ? 'Məhsulu redaktə et' : 'Yeni məhsul'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Məhsul adı *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Təsvir</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="price">Qiymət (AZN) *</Label>
                      <Input
                        id="price"
                        name="price"
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="stock">Stok</Label>
                      <Input
                        id="stock"
                        name="stock"
                        type="number"
                        value={formData.stock}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  {categories.length > 0 && (
                    <div>
                      <Label htmlFor="category_id">Kateqoriya</Label>
                      <select
                        id="category_id"
                        name="category_id"
                        value={formData.category_id}
                        onChange={handleChange}
                        className="w-full mt-1 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Kateqoriya seçin</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <Label>Şəkillər və Videolar</Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Maksimum 10 fayl (şəkil: 5MB, video: 100MB)
                    </p>
                    <MultiMediaUpload
                      currentMedia={formData.media}
                      onUploadComplete={(media) => setFormData({ ...formData, media })}
                      maxFiles={10}
                      allowVideo={true}
                      folder="products"
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Saxlanılır...' : (editingProduct ? 'Yenilə' : 'Əlavə et')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Limit warning */}
        {!limitInfo.canAdd && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-sm text-red-700">
                Məhsul limitinə çatdınız ({limitInfo.maxProducts}). Daha çox məhsul əlavə etmək üçün admin ilə əlaqə saxlayın.
              </p>
            </div>
          </div>
        )}

        {products.length === 0 ? (
          <div className="bg-white rounded-xl border border-border p-12 text-center">
            <p className="text-muted-foreground">Heç bir məhsul yoxdur. İlk məhsulunuzu əlavə edin!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => {
              const mediaCount = getMediaCount(product);
              const mainImage = product.media?.[0]?.url || product.media?.[0] || product.image_url;
              
              return (
                <div
                  key={product.id}
                  className="bg-white rounded-xl border border-border overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="relative">
                    {mainImage ? (
                      <img
                        src={mainImage}
                        alt={product.name}
                        className="w-full h-48 object-cover"
                      />
                    ) : (
                      <div className="w-full h-48 bg-muted flex items-center justify-center">
                        <ImageIcon className="w-12 h-12 text-muted-foreground" />
                      </div>
                    )}
                    {/* Media count badge */}
                    {(mediaCount.images > 1 || mediaCount.videos > 0) && (
                      <div className="absolute bottom-2 right-2 flex gap-1">
                        {mediaCount.images > 0 && (
                          <span className="bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                            <ImageIcon className="w-3 h-3" />
                            {mediaCount.images}
                          </span>
                        )}
                        {mediaCount.videos > 0 && (
                          <span className="bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                            <Video className="w-3 h-3" />
                            {mediaCount.videos}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    {product.category_id && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded mb-2 inline-block">
                        {getCategoryName(product.category_id)}
                      </span>
                    )}
                    <h3 className="font-semibold text-lg mb-1">{product.name}</h3>
                    {product.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {product.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-bold text-primary">{product.price} AZN</span>
                      <span className="text-sm text-muted-foreground">Stok: {product.stock || 0}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleEdit(product)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Redaktə
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(product.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
